import { useState } from "react";
import { Download, Truck } from "lucide-react";
import { ewayApi, gstLiveApi, type EwayEntry, type Voucher } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote } from "../ui/bits";
import { download } from "../ui/download";
import type { Option } from "../ui/Combobox";
import { EWayPrompt, MODES } from "../vouchers/EWay";
import { cleanEway, ewayProblem } from "../vouchers/eway";

// On a posted sales invoice: its e-way bill. Until the GSP connection is built, the request is downloaded for the
// e-way bill portal (bulk upload, or Part B by IRN) and the number it gives is entered here.
export function EWayPanel({ v, states }: { v: Voucher; states: Option[] }) {
  const { company } = useCompany();
  const [rev, setRev] = useState(0);
  const e = useLoad(() => ewayApi.get(company.id, v.id), [company.id, v.id, rev]);
  const [edit, setEdit] = useState<EwayEntry | null>(null);
  const [no, setNo] = useState<{ number: string; upto: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => { setErr(null); setBusy(true); try { await fn(); setEdit(null); setNo(null); setRev((r) => r + 1); } catch (x) { setErr(errText(x)); } finally { setBusy(false); } };
  const d = e.data;
  if (!d || (d.status === "not_required" && !d.number && !d.choice)) return null;
  const file = v.number?.replace(/\//g, "-");
  const t = d.transport;

  const saveEdit = () => {
    const p = ewayProblem(d, edit);
    if (p) return setErr(p);
    const c = cleanEway(edit)!;
    if (c.choice === "have") return run(() => ewayApi.record(company.id, v.id, c.number!, c.valid_upto ?? null));
    if (c.choice === "generate") return run(() => ewayApi.transport(company.id, v.id, c.transport!));
    return run(() => ewayApi.choose(company.id, v.id, c.choice, c.reason ?? undefined));
  };
  const getRequest = () => run(async () => {
    const r = await ewayApi.request(company.id, v.id);
    download(`ewaybill-${file}.json`, JSON.stringify(r.request, null, 2));
    setTimeout(() => setNo({ number: "", upto: r.valid_upto.slice(0, 16) }), 0);
  });

  return (
    <div className="card panel actions-panel eway-panel">
      <h3><Truck size={16} aria-hidden="true" /> E-way bill</h3>
      {d.number ? <p className="small">No. <b className="mono">{d.number}</b>{d.valid_upto ? ` · valid to ${new Date(d.valid_upto).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}</p> : <>
        <p className="small">{d.status === "not_required" ? "Not needed by value" : d.status === "check" ? "May be needed" : <b className="warn">Needed, not made yet</b>}
          <span className="muted"> · {d.reason}</span></p>
        {d.choice === "not_needed" && <p className="muted small">Marked not needed: {d.choice_reason}</p>}
        {d.choice === "later" && <p className="muted small">Left for later: the goods can't move until it is made.</p>}
        {t && d.choice === "generate" && <p className="small">{MODES.find((m) => m[0] === t.mode)?.[1]} · {t.distance_km} km{t.vehicle_no ? ` · ${t.vehicle_no}` : ""}
          {t.transporter_name ? ` · ${t.transporter_name}` : ""}{t.ship_to_state ? ` · ship to ${t.ship_to_place ?? ""} (${t.ship_to_state})` : ""}</p>}
        {d.can.act && !edit && !no && <div className="row">
          {t && d.choice === "generate" && <button className="primary" disabled={busy} onClick={() => void getRequest()}><Download size={15} aria-hidden="true" /> {d.irn ? "Part B request (by IRN)" : "Request for the portal"}</button>}
          {t && d.choice === "generate" && <button className="primary" disabled={busy} onClick={() => void run(() => gstLiveApi.ewaybill(company.id, v.id))}>{busy ? "Asking…" : "Generate it now"}</button>}
          {t && d.choice === "generate" && <button disabled={busy} onClick={() => setNo({ number: "", upto: "" })}>Enter the number</button>}
          <button className={t && d.choice === "generate" ? "link small" : ""} disabled={busy}
            onClick={() => setEdit(d.choice === "generate" && t ? { choice: "generate", transport: { ...t } } : { choice: "generate" })}>
            {t && d.choice === "generate" ? "Change transport" : "Make it, or enter the number…"}</button>
        </div>}
        {d.choice === "generate" && t && !no && <p className="muted small">Upload the request at ewaybillgst.gov.in (e-Waybill › Generate Bulk), then enter the 12-digit number it gives.</p>}
      </>}
      {no && <div className="stack">
        <label>E-way bill no.<input className="mono" inputMode="numeric" maxLength={12} value={no.number} autoFocus onChange={(x) => setNo({ ...no, number: x.target.value.replace(/\D/g, "") })} /></label>
        <label>Valid up to<input type="datetime-local" value={no.upto} onChange={(x) => setNo({ ...no, upto: x.target.value })} /></label>
        <div className="row"><button className="primary" disabled={busy} onClick={() => void run(() => ewayApi.record(company.id, v.id, no.number, no.upto ? new Date(no.upto).toISOString() : null))}>Save</button>
          <button className="link" onClick={() => setNo(null)}>Cancel</button></div></div>}
      {edit !== null && <div className="stack">
        <EWayPrompt check={d} value={edit} onChange={(x) => setEdit(x ?? { choice: "generate" })} states={states} />
        <div className="row"><button className="primary" disabled={busy} onClick={saveEdit}>Save</button><button className="link" onClick={() => setEdit(null)}>Cancel</button></div></div>}
      <ErrorNote text={err} />
    </div>
  );
}
